import i18nFactory from '@yandex-int/i18n';
import fetchJsonp from 'fetch-jsonp';
import { Epic, ofType } from 'redux-observable';
import { EMPTY, from, Observable, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import {
    FETCH_HEADER_INFO,
    FETCH_HEADER_WALLET_INFO,
    FETCH_INFO_BLOCK_DATA,
    FETCH_NEXT_INFO_BLOCK_NEWS_ITEM,
    FETCH_USER_ACCOUNTS_INFO,
    fetchInfoBlockData,
    SEND_GRID_FEEDBACK,
    SendGridFeedback,
    setHeaderWalletInfo,
    setInfoBlockNewsError,
    setUnauthorizedHeaderData,
    setUserAccountsInfoFetchError,
    updateHeaderInfo,
    updateInfoBlockData,
    updateInfoBlockNewsData,
    updateUserAccountsInfo
} from '../../actions/header';
import { Action } from '../../actions/index';
import { mapApiWalletsToWalletData, mapUserAccountsInfo } from '../../mappers/common/common';
import {
    mapCurrentUserAndClientApiData,
    mapCurrentUserApiData,
    mapInfoBlockApiData,
    mapInfoBlockApiNewsData
} from '../../mappers/header/header';
import { LangUtils } from '../../service/Lang/Lang';
import '../../service/Rx';
import { EpicDeps } from '../../service/StoreFactory';
import { HeaderInfoBlockApiData, HeaderInfoBlockNewsItemApiData } from '../../typings/header';
import { State } from '../../typings/state';

import * as keyset from './header.i18n';

const i18n = i18nFactory(keyset);

const fetchInfoBlockDataEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(FETCH_INFO_BLOCK_DATA).mergeMap(
        (): Observable<Action> => {
            const state = store$.value;
            const {
                header: { clientId, chiefUserId }
            } = state;

            if (!clientId || !chiefUserId) {
                return Observable.of();
            }

            return Observable.fromPromise(
                deps.restApiManager.fetchNewsData(clientId, LangUtils.getCurrentLang(), chiefUserId)
            )
                .map((res: HeaderInfoBlockApiData) => updateInfoBlockData(mapInfoBlockApiData(res[0])))
                .catch(() => Observable.of(setInfoBlockNewsError(i18n('try-later'))));
        }
    );
};

const fetchInfoBlockNewsItemEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(FETCH_NEXT_INFO_BLOCK_NEWS_ITEM).mergeMap(
        (): Observable<Action> => {
            const state = store$.value;
            const {
                header: {
                    infoBlockData: {
                        news: { itemsQueue }
                    }
                }
            } = state;

            const next = itemsQueue.shift();

            if (!next) {
                return Observable.of();
            }

            return Observable.fromPromise(deps.restApiManager.fetchNewsDataById(next.id))
                .mergeMap((res: HeaderInfoBlockNewsItemApiData) => {
                    const result = res.data;

                    if (!result) {
                        return EMPTY;
                    }

                    return of(updateInfoBlockNewsData(mapInfoBlockApiNewsData(result, res.new)));
                })
                .catch(() => of(setInfoBlockNewsError(i18n('try-later'))));
        }
    );
};

const sendFeedbackEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(SEND_GRID_FEEDBACK).mergeMap((action: SendGridFeedback) => {
        return Observable.fromPromise(deps.restApiManager.sendGridFeedback(action.message)).mergeMap(() => EMPTY);
    });
};

const fetchUserAccountsInfoEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(FETCH_USER_ACCOUNTS_INFO),
        mergeMap(
            (): Observable<Action> => {
                return from(deps.commonDataProvider.fetchUserAccountsInfo()).pipe(
                    mergeMap(
                        (response: fetchJsonp.Response): Observable<Action> => {
                            return from(response.json()).pipe(
                                map(json => {
                                    return updateUserAccountsInfo(mapUserAccountsInfo(json));
                                })
                            );
                        }
                    ),
                    catchError((): Observable<Action> => Observable.of(setUserAccountsInfoFetchError()))
                );
            }
        )
    );
};

const fetchHeaderInfoEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(FETCH_HEADER_INFO).mergeMap(
        (): Observable<Action> => {
            const { authorizationData, login } = store$.value.commonData;

            if (!authorizationData) {
                return Observable.of(setUnauthorizedHeaderData());
            }

            return Observable.fromPromise(
                Promise.all([
                    deps.commonDataProvider.fetchHeaderInfo(authorizationData.login),
                    deps.commonDataProvider.fetchHeaderMenu()
                ])
            )
                .mergeMap(([infoResult, menuResult]) => {
                    if (authorizationData.login !== login) {
                        return Observable.fromPromise(deps.commonDataProvider.fetchHeaderClientInfo(login)).mergeMap(
                            clientRes =>
                                Observable.concat(
                                    Observable.of(
                                        updateHeaderInfo(
                                            mapCurrentUserAndClientApiData(
                                                infoResult.data,
                                                menuResult.data,
                                                clientRes.data
                                            )
                                        )
                                    ),
                                    Observable.of(fetchInfoBlockData())
                                )
                        );
                    }

                    return Observable.concat(
                        Observable.of(updateHeaderInfo(mapCurrentUserApiData(infoResult.data, menuResult.data))),
                        Observable.of(fetchInfoBlockData())
                    );
                })
                .catch(err => Observable.of(setUnauthorizedHeaderData()));
        }
    );
};

const fetchHeaderWalletInfoEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(FETCH_HEADER_WALLET_INFO).switchMap(
        (): Observable<Action> => {
            const { authorizationData } = store$.value.commonData;

            if (!authorizationData) {
                return EMPTY;
            }

            return Observable.fromPromise(deps.commonDataProvider.fetchHeaderWalletInfo(authorizationData.login))
                .map(response => response.data.client.wallets)
                .switchMap(wallets => {
                    const wallet = mapApiWalletsToWalletData(wallets);

                    return Observable.of(setHeaderWalletInfo(wallet));
                })
                .catch(() => {
                    return EMPTY;
                });
        }
    );
};

export {
    fetchInfoBlockDataEpic,
    fetchHeaderWalletInfoEpic,
    fetchInfoBlockNewsItemEpic,
    fetchUserAccountsInfoEpic,
    fetchHeaderInfoEpic,
    sendFeedbackEpic
};
